import React, { useCallback, useEffect, useState } from 'react';
import { api, fetchWithAuth } from '../config/api';
import './CrmDirectoryPanel.css';

function formatPersonName(p) {
  if (!p) return '—';
  if (p.name && String(p.name).trim()) return String(p.name).trim();
  const first = p.first_name || '';
  const last = p.last_name_obfuscated || p.last_name || '';
  return `${first} ${last}`.trim() || '—';
}

function displayPersonAfterEnrich(p, enrichedPerson) {
  if (!enrichedPerson) return formatPersonName(p);
  if (enrichedPerson.name) return enrichedPerson.name;
  const a = enrichedPerson.first_name || '';
  const b = enrichedPerson.last_name || '';
  const full = `${a} ${b}`.trim();
  return full || formatPersonName(p);
}

function formatPersonPhones(p) {
  if (!p) return null;
  if (p.sanitized_phone) return String(p.sanitized_phone);
  const arr = p.phone_numbers;
  if (Array.isArray(arr) && arr.length) {
    const x = arr[0];
    if (x && (x.sanitized_number || x.raw_number)) {
      return String(x.sanitized_number || x.raw_number);
    }
  }
  return null;
}

function mergePersonRow(p, enrichedOverride) {
  if (!enrichedOverride) return p;
  return { ...p, ...enrichedOverride, id: p.id };
}

/** Maps apollo_points.color slug to contractor / certification type (same logic as map popups). */
const CONTRACTOR_TYPE_LABELS = {
  azul: 'Blue (in CT Distributors)',
  morado: 'Not certified',
  'azul-oscuro': 'SSM Certified',
  verde: 'SM Certified',
  'naranja-oscuro': 'STAR Certified',
  gris: 'SMARTCOAT Certified',
  'rosa-fucsia': '5 Star Company',
  rojo: 'Silver Star Siding'
};

function formatContractorType(color) {
  if (color == null || String(color).trim() === '') return '—';
  const key = String(color).trim();
  return CONTRACTOR_TYPE_LABELS[key] || key;
}

function normalizeStr(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function domainFromWebsite(website) {
  const w0 = normalizeStr(website);
  if (!w0) return null;
  let w = w0;
  if (!/^https?:\/\//i.test(w)) w = `https://${w}`;
  try {
    const host = new URL(w).hostname;
    return host.replace(/^www\./i, '') || null;
  } catch {
    return null;
  }
}

/** Valid payload for Apollo People Search */
function getApolloSearchPayload(row) {
  const org = normalizeStr(row.apollo_account_id);
  const dom = normalizeStr(row.domain) || domainFromWebsite(row.website);
  return {
    apollo_account_id: org || undefined,
    domain: dom || undefined
  };
}

function canSearchApolloPeople(row) {
  const p = getApolloSearchPayload(row);
  if (p.apollo_account_id || p.domain) return true;
  if (normalizeStr(row.company) || normalizeStr(row.company_name)) return true;
  const cne = normalizeStr(row.company_name_for_emails);
  if (cne) return true;
  return false;
}

const CrmDirectoryPanel = ({ mode }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [peopleByCompany, setPeopleByCompany] = useState({});
  const [peopleLoading, setPeopleLoading] = useState({});
  const [peopleError, setPeopleError] = useState({});
  /** person_id Apollo → { loading, error, person } */
  const [enrichedByPersonId, setEnrichedByPersonId] = useState({});

  const title = mode === 'crm' ? 'Certified' : 'Not certified';
  const subtitle =
    mode === 'crm'
      ? 'CertainTeed certified businesses (SSM, SM, STAR, etc.). Ranked by Hispanic population share in the business ZIP.'
      : 'Non-certified businesses (purple). Ranked by Hispanic population share in the business ZIP.';

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpandedId(null);
    setPeopleByCompany({});
    setPeopleError({});
    try {
      const res = await fetchWithAuth(api.crmDirectory(mode, 250));
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCompanies(data.companies || []);
    } catch (e) {
      setError(e.message || String(e));
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const loadPeople = async (row) => {
    const id = row.id;
    const payload = getApolloSearchPayload(row);
    if (!canSearchApolloPeople(row)) {
      setPeopleError((s) => ({
        ...s,
        [id]:
          'No company name (or company_name_for_emails), Apollo account ID, or website/domain to search.'
      }));
      return;
    }
    setPeopleLoading((s) => ({ ...s, [id]: true }));
    setPeopleError((s) => ({ ...s, [id]: null }));
    try {
      const res = await fetchWithAuth(api.apolloIoPeopleSearch(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          company: row.company,
          company_name_for_emails: row.company_name_for_emails,
          company_city: row.company_city,
          company_state: row.company_state,
          page: 1,
          per_page: 50
        })
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.apollo_detail ? ` Apollo: ${data.apollo_detail}` : '';
        throw new Error((data.error || data.message || JSON.stringify(data)) + detail);
      }
      setPeopleByCompany((s) => ({
        ...s,
        [id]: { people: data.people || [], meta: data.meta || null }
      }));
    } catch (e) {
      setPeopleError((s) => ({ ...s, [id]: e.message || String(e) }));
    } finally {
      setPeopleLoading((s) => ({ ...s, [id]: false }));
    }
  };

  const toggleRow = (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(row.id);
    if (!peopleByCompany[row.id] && !peopleLoading[row.id]) {
      loadPeople(row);
    }
  };

  const enrichPerson = async (p, companyRow) => {
    const pid = p.id;
    if (!pid) return;
    setEnrichedByPersonId((s) => ({
      ...s,
      [pid]: { ...(s[pid] || {}), loading: true, error: null }
    }));
    try {
      const payload = getApolloSearchPayload(companyRow);
      const res = await fetchWithAuth(api.apolloIoPeopleEnrich(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: pid,
          reveal_personal_emails: true,
          domain: payload.domain,
          organization_name: companyRow.company || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.apollo_detail ? ` ${data.apollo_detail}` : '';
        throw new Error((data.error || data.message || JSON.stringify(data)) + detail);
      }
      setEnrichedByPersonId((s) => ({
        ...s,
        [pid]: { loading: false, error: null, person: data.person || null }
      }));
    } catch (e) {
      setEnrichedByPersonId((s) => ({
        ...s,
        [pid]: { loading: false, error: e.message || String(e), person: null }
      }));
    }
  };

  return (
    <div className="crm-directory">
      <div className="crm-directory__header">
        <h1>{title}</h1>
        <p className="crm-directory__subtitle">{subtitle}</p>
        <button type="button" className="crm-directory__refresh" onClick={loadCompanies} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh list'}
        </button>
      </div>

      {error && <div className="crm-directory__error">{error}</div>}

      {loading && companies.length === 0 ? (
        <div className="crm-directory__loading">Loading companies…</div>
      ) : (
        <div className="crm-directory__table-wrap">
          <table className="crm-directory__table">
            <thead>
              <tr>
                <th>Ranking</th>
                <th>% Hispanic (area)</th>
                <th>Company</th>
                <th>City</th>
                <th>State</th>
                <th>ZIP</th>
                <th>Type</th>
                <th>Apollo / domain</th>
                <th className="crm-directory__col-action" />
              </tr>
            </thead>
            <tbody>
              {companies.map((row) => (
                <React.Fragment key={row.id}>
                  <tr className={expandedId === row.id ? 'is-open' : ''}>
                    <td>{row.importance_rank}</td>
                    <td>
                      {row.hispanic_percentage != null
                        ? `${Number(row.hispanic_percentage).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="crm-directory__cell-company">
                      {row.company || row.company_name_for_emails || '—'}
                    </td>
                    <td>{row.company_city || '—'}</td>
                    <td>{row.company_state || '—'}</td>
                    <td>{row.company_postal_code || row.zip5 || '—'}</td>
                    <td>{formatContractorType(row.color)}</td>
                    <td className="crm-directory__cell-meta">
                      <span className="crm-directory__mono">{row.apollo_account_id || '—'}</span>
                      {row.domain ? <span className="crm-directory__domain">{row.domain}</span> : null}
                      {row.website ? (
                        <span className="crm-directory__website" title="Used to infer domain when missing">
                          {row.website.length > 40 ? `${row.website.slice(0, 40)}…` : row.website}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`crm-directory__expand${!canSearchApolloPeople(row) ? ' crm-directory__expand--warn' : ''}`}
                        title={
                          canSearchApolloPeople(row)
                            ? 'Look up people in Apollo'
                            : 'No company name or data to search'
                        }
                        onClick={() => toggleRow(row)}
                      >
                        {expandedId === row.id ? 'Hide' : 'People'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr className="crm-directory__detail-row">
                      <td colSpan={9}>
                        {peopleLoading[row.id] && <p>Searching Apollo for people…</p>}
                        {peopleError[row.id] && (
                          <p className="crm-directory__error">{peopleError[row.id]}</p>
                        )}
                        {!peopleLoading[row.id] &&
                          !(peopleByCompany[row.id]?.people || []).length &&
                          !peopleError[row.id] && (
                            <p>No people found for this company in Apollo.</p>
                          )}
                        {(peopleByCompany[row.id]?.people || []).length > 0 && (
                          <>
                            {peopleByCompany[row.id]?.meta?.resolved_via === 'organization_search' && (
                              <p className="crm-directory__meta">
                                Organization resolved by name search in Apollo (verify it is the correct company).
                              </p>
                            )}
                            {peopleByCompany[row.id]?.meta?.resolved_via === 'keywords_search' && (
                              <p className="crm-directory__meta">
                                {peopleByCompany[row.id].meta.hint ||
                                  'Keyword search: results may include contacts outside this company.'}
                              </p>
                            )}
                            {peopleByCompany[row.id]?.meta?.enrichment && (
                              <p className="crm-directory__meta">
                                Contact data:{' '}
                                <strong>{peopleByCompany[row.id].meta.enrichment.cached}</strong> from local cache,{' '}
                                <strong>{peopleByCompany[row.id].meta.enrichment.fetched}</strong> new from Apollo
                                {peopleByCompany[row.id].meta.enrichment.failed ? (
                                  <>
                                    {' '}
                                    · <strong>{peopleByCompany[row.id].meta.enrichment.failed}</strong> incomplete
                                  </>
                                ) : null}
                              </p>
                            )}
                            <div className="crm-directory__subtable-wrap">
                            <table className="crm-directory__subtable">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Title</th>
                                  <th>Email</th>
                                  <th>LinkedIn</th>
                                  <th>Phone</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {(peopleByCompany[row.id].people || []).map((p) => {
                                  const en = enrichedByPersonId[p.id];
                                  const ep = en?.person;
                                  const rowPerson = mergePersonRow(p, ep);
                                  const emailShow = rowPerson.email;
                                  const titleShow = rowPerson.title;
                                  const phoneShow = formatPersonPhones(rowPerson);
                                  return (
                                    <tr key={p.id}>
                                      <td>{displayPersonAfterEnrich(p, ep)}</td>
                                      <td>{titleShow || '—'}</td>
                                      <td>
                                        {en?.loading && (
                                          <span className="crm-directory__enrich-loading">Fetching data…</span>
                                        )}
                                        {en?.error && (
                                          <span className="crm-directory__enrich-err">{en.error}</span>
                                        )}
                                        {!en?.loading && !en?.error && emailShow && (
                                          <>
                                            <span>{emailShow}</span>
                                            {rowPerson.email_status && (
                                              <span className="crm-directory__email-status">
                                                {' '}
                                                ({rowPerson.email_status})
                                              </span>
                                            )}
                                          </>
                                        )}
                                        {!en?.loading && !en?.error && !emailShow && (
                                          <span className="crm-directory__email-muted">Pending</span>
                                        )}
                                      </td>
                                      <td>
                                        {rowPerson.linkedin_url ? (
                                          <a
                                            href={rowPerson.linkedin_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="crm-directory__link-inline"
                                          >
                                            Profile
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td>{phoneShow || '—'}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="crm-directory__enrich-btn"
                                          disabled={en?.loading}
                                          onClick={() => enrichPerson(p, row)}
                                        >
                                          {en?.loading ? '…' : emailShow ? 'Refresh' : 'Complete data'}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CrmDirectoryPanel;
